import * as Highcharts from 'highcharts';
import { TranslateService } from '@ngx-translate/core';
import { MONTHS_SHORT } from '../../../../shared/common/enums';
import dayjs from 'dayjs';

export const colors = ['#003259', '#006085', '#00A4BA', '#5DEAE4', '#35cfd5', '#94f8ec'];
export const markerSymbols = ['circle', 'square', 'diamond', 'triangle', 'triangle-down'];

export const commonChartOptions: Highcharts.Options = {
  chart: {
    type: 'spline',
    height: '500',
  },
  credits: {
    enabled: false,
  },
  navigator: {
    enabled: false,
  },
  rangeSelector: {
    enabled: false,
  },
  legend: {
    enabled: false,
    useHTML: true,
    itemMarginBottom: 8,
    itemMarginTop: 8,
    itemStyle: {
      fontSize: '13px',
    },
    labelFormatter(): string {
      return (
        '<span style="position: relative; top: -3px; font-size: 1.2em;" title="' +
        this.name +
        '">' +
        this.name +
        '</span>'
      );
    },
  },
  lang: {
    noData: '',
  },
  noData: {
    style: {
      fontWeight: 'bold',
      fontSize: '15px',
      color: '#303030',
    },
  },
  title: {
    text: null,
  },
  series: [],
  yAxis: [
    {
      title: { text: '' },
      max: null,
      opposite: false,
    },
    {
      title: { text: '' },
      opposite: true,
    },
  ],
};

export const dayChartOptions: Highcharts.Options = {
  xAxis: {
    min: 0,
    max: 95,
    crosshair: true,
    categories: [
      '00:00',
      '00:15',
      '00:30',
      '00:45',
      '01:00',
      '01:15',
      '01:30',
      '01:45',
      '02:00',
      '02:15',
      '02:30',
      '02:45',
      '03:00',
      '03:15',
      '03:30',
      '03:45',
      '04:00',
      '04:15',
      '04:30',
      '04:45',
      '05:00',
      '05:15',
      '05:30',
      '05:45',
      '06:00',
      '06:15',
      '06:30',
      '06:45',
      '07:00',
      '07:15',
      '07:30',
      '07:45',
      '08:00',
      '08:15',
      '08:30',
      '08:45',
      '09:00',
      '09:15',
      '09:30',
      '09:45',
      '10:00',
      '10:15',
      '10:30',
      '10:45',
      '11:00',
      '11:15',
      '11:30',
      '11:45',
      '12:00',
      '12:15',
      '12:30',
      '12:45',
      '13:00',
      '13:15',
      '13:30',
      '13:45',
      '14:00',
      '14:15',
      '14:30',
      '14:45',
      '15:00',
      '15:15',
      '15:30',
      '15:45',
      '16:00',
      '16:15',
      '16:30',
      '16:45',
      '17:00',
      '17:15',
      '17:30',
      '17:45',
      '18:00',
      '18:15',
      '18:30',
      '18:45',
      '19:00',
      '19:15',
      '19:30',
      '19:45',
      '20:00',
      '20:15',
      '20:30',
      '20:45',
      '21:00',
      '21:15',
      '21:30',
      '21:45',
      '22:00',
      '22:15',
      '22:30',
      '22:45',
      '23:00',
      '23:15',
      '23:30',
      '23:45',
    ],
  },
};

export function getXAxisTitleTextChartOptions(
  translateService: TranslateService,
  key: string
): Highcharts.Options {
  let word = translateService.instant(key);
  word = word.charAt(0).toUpperCase() + word.slice(1); // Capitalize

  return {
    xAxis: {
      crosshair: true,
      title: {
        text: word,
      },
    },
  };
}

export function getFormatterForLocalizedDate(
  translateService: TranslateService,
  value: string | number
): string {
  const date = new Date(value);

  return `${date.getDate()}. ${translateService.instant(MONTHS_SHORT[date.getMonth()])}`;
}

export function getChartTooltip(
  tooltipFormatterContextObject: Highcharts.TooltipFormatterContextObject,
  locale: string,
  format: string
): string {
  const wrapperStyles = `
            display: flex;
            padding: 8px 16px;
            flex-direction: column;
            gap: 1px;
          `;

  const separatorStyle = `
            display: flex;
            width: 100%;
            background-color: #e7e7e7;
            height: 1px;
            margin: 6px 0;
          `;

  const entryStyles = `
            width: 100%;
            display: flex;
            justify-content: space-between;
          `;

  const unitStyles = `
            padding-left: 16px;
            font-weight: bold;
          `;

  const content = tooltipFormatterContextObject.points.reduce(function (acc, point: any, index) {
    const valueLine = `<div style="${entryStyles}"><span><span style="color: ${
      point.series.color
    }; margin-right: 5px">\u25A0</span>${point.series.name}</span><span style="${unitStyles}">${
      Math.round(point.y * 100) / 100
    }${point.series['tooltipOptions'].valueSuffix}</span></div>`;

    if (index === 0) {
      const dateText = dayjs(new Date(point.key)).locale(locale).format(format);

      const dateLine = `<div style="${entryStyles}"><span>${dateText}</span></div>`;
      const separator = `<div style="${separatorStyle}"></div>`;

      return `${dateLine}${separator}${valueLine}`;
    }

    return `${acc}${valueLine}`;
  }, '<b>' + tooltipFormatterContextObject.x + '</b>');

  const wrapper = `<span style="${wrapperStyles};">${content}</span>`;

  return wrapper;
}
